// Sprint work-segment-collapse (PR-B, D3 item 13, B6) — the muted-header
// chrome shared by `AggregationCard` (a run of tool_use messages) and
// `WorkSegment` (a run of render items between two agent messages). Cut
// out of `AggregationCard.tsx` byte-identically (the icon rail, the
// button and its children); the derivation block, `AggregationStatusGlyph`
// and the children panel stayed behind and are passed in through props.
//
// NB-8 — `overflow-hidden` moved from the whole inner column (harmless
// there: `AggregationCard`'s panel only ever holds `FounderToolRow`s that
// never overflow) to THIS row only. A work-segment panel can hold a
// `SubagentPanel`, a `WorkflowPanel`, a `.md`-path chip, and
// `ToolCallRow`'s rail connector (`absolute … bottom-0`, intentionally
// painting to its parent's edge) — clipping the whole column at the
// segment level would be a visible regression. Scoped to the header row,
// the truncating headline still gets a bounded box via `min-w-0` on the
// column + `overflow-hidden` here.

import type { ReactNode } from 'react';
import { Icon, type IconName } from '../atoms/Icon';

export interface CollapseHeaderRowProps {
  icon: IconName;
  iconClassName: string;
  headline: ReactNode;
  headlineClassName: string;
  failureLabel?: string | null;
  failureTestId?: string;
  meta?: string | null;
  metaTestId?: string;
  /** AggregationCard: <AggregationStatusGlyph/>. WorkSegment: undefined. */
  trailing?: ReactNode;
  open: boolean;
  onToggle: () => void;
  /** WorkSegment: "py-2" (D9 rhythm step). AggregationCard: undefined. */
  rootClassName?: string;
  rootTestId: string;
  headerTestId: string;
  /** data-tool/-count/-error-count (AggregationCard) | data-segment-id/-folded/-failures (WorkSegment). */
  rootProps?: Record<string, string | number | undefined>;
  ariaLabel?: string;
  /** The open panel, rendered by the caller. */
  children?: ReactNode;
}

export function CollapseHeaderRow({
  icon,
  iconClassName,
  headline,
  headlineClassName,
  failureLabel,
  failureTestId,
  meta,
  metaTestId,
  trailing,
  open,
  onToggle,
  rootClassName,
  rootTestId,
  headerTestId,
  rootProps,
  ariaLabel,
  children,
}: CollapseHeaderRowProps) {
  return (
    <div
      className={`fade-in-0 slide-in-from-top-2 animate-in flex gap-2 text-body${rootClassName ? ` ${rootClassName}` : ''}`}
      data-testid={rootTestId}
      {...rootProps}
    >
      <div className="relative mt-1 shrink-0">
        <Icon name={icon} className={iconClassName} size={14} aria-hidden="true" />
        <div className="-mx-px absolute top-6 bottom-0 left-1/2 w-px bg-border" />
      </div>
      <div className="flex-1 min-w-0 space-y-1">
        <div className="overflow-hidden">
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={open}
            aria-label={ariaLabel}
            className="flex w-full items-center gap-2 py-0.5 rounded-md text-left transition-colors duration-100 hover:bg-accent focus:outline-none focus:ring-1 focus:ring-border"
            data-testid={headerTestId}
          >
            <span className={`flex-1 truncate text-body ${headlineClassName}`}>{headline}</span>
            {failureLabel && (
              <span className="shrink-0 text-status-error" data-testid={failureTestId}>
                {failureLabel}
              </span>
            )}
            {meta && (
              <span
                className="shrink-0 text-caption text-muted-foreground/70 tabular-nums"
                data-testid={metaTestId}
              >
                {meta}
              </span>
            )}
            {trailing}
            <span className="text-muted-foreground/45 shrink-0" aria-hidden="true">
              {open ? <Icon name="ChevronDown" size={12} /> : <Icon name="ChevronRight" size={12} />}
            </span>
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
